import React, { useMemo } from 'react';
import { TrendingUp, TrendingDown } from 'lucide-react';
import LoadingSpinner from './LoadingSpinner';
import { useFundData } from '../context/FundDataContext';
import {
  holdingDisplayName,
  holdingMarketValue,
  holdingGainLossPct,
  portfolioPercent,
  positionHoldings,
  summaryRows,
  toNumber,
} from '../utils/calc';
import { formatCurrency, formatPercent, formatHoldingsAsOf } from '../utils/format';

function Portfolio() {
  const { holdings, fundMeta, performance, ready } = useFundData();
  const cash = fundMeta?.cash ?? 0;

  const contributedCapital = useMemo(() => {
    const valid = performance.filter(
      (row) => row.date && row.contributedCapital !== undefined && row.contributedCapital !== null
    );
    if (valid.length === 0) return null;
    return toNumber(valid[valid.length - 1].contributedCapital);
  }, [performance]);

  const renderGainLoss = (pct) => {
    if (pct === null || pct === undefined) return <span style={{ color: 'var(--c-faint)' }}>--</span>;
    const isNegative = pct < 0;
    return (
      <span className={`c-gain-loss ${isNegative ? 'c-neg' : 'c-pos'}`}>
        {isNegative ? <TrendingDown size={14} /> : <TrendingUp size={14} />}
        {`${Math.abs(pct).toFixed(1)}%`}
      </span>
    );
  };

  if (!ready) {
    return <LoadingSpinner text="Loading portfolio..." />;
  }

  const positions = positionHoldings(holdings);
  const legacyCashRow = holdings.find((h) => holdingDisplayName(h).toLowerCase() === 'cash');
  const effectiveCash =
    (toNumber(cash) || 0) || (legacyCashRow ? holdingMarketValue(legacyCashRow) : 0);

  const summaries = summaryRows(positions, effectiveCash, contributedCapital);

  return (
    <>
      <div className="client-page-head">
        <h1 className="client-page-title">Portfolio</h1>
        <p className="client-page-sub">Holdings as of {formatHoldingsAsOf(holdings)}</p>
      </div>

      <div className="c-table-wrap">
        <table className="c-table">
          <thead>
            <tr>
              <th>Equities</th>
              <th>Ticker</th>
              <th>Div yield</th>
              <th className="num">Cost basis</th>
              <th className="num">Market value</th>
              <th className="num">% of portfolio</th>
              <th className="num">Gain / loss</th>
            </tr>
          </thead>
          <tbody>
            {positions.map((holding, index) => (
              <tr key={holding.id || index}>
                <td style={{ fontWeight: 500 }}>{holdingDisplayName(holding)}</td>
                <td>
                  {holding.ticker && holding.ticker !== '--' && (
                    <span className="c-ticker">{holding.ticker}</span>
                  )}
                </td>
                <td>{formatPercent(holding.dividendYield)}</td>
                <td className="num">{formatCurrency(holding.costBasis)}</td>
                <td className="num" style={{ fontWeight: 600 }}>
                  {formatCurrency(holdingMarketValue(holding))}
                </td>
                <td className="num">{formatPercent(portfolioPercent(holding, positions, effectiveCash))}</td>
                <td className="num">{renderGainLoss(holdingGainLossPct(holding))}</td>
              </tr>
            ))}

            {summaries.map((row) => (
              <tr key={row.name} className="summary-row">
                <td style={{ fontWeight: 600 }}>{row.name}</td>
                <td />
                <td />
                <td className="num">{formatCurrency(row.costBasis)}</td>
                <td className="num" style={{ fontWeight: 600 }}>{formatCurrency(row.marketValue)}</td>
                <td className="num">{formatPercent(row.portfolioPercent)}</td>
                <td className="num">{renderGainLoss(row.gainLossPct)}</td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </>
  );
}

export default Portfolio;
